import './index.css'
import memoize from 'memoize-one'

import React, { useRef } from 'react'
import { Scrollbars } from 'react-custom-scrollbars'
import withScrolling, {
  createVerticalStrength,
  useDndScrolling,
} from 'react-dnd-scrolling'
const vStrength = createVerticalStrength(100)
const ScrollingComponent = withScrolling('div')

// react-dnd-scrolling@1.3.x wraps components in a function component that swallows
// forwarded refs. react-custom-scrollbars needs the inner DOM node to compute
// scroll offsets, so we replicate withScrolling here via useDndScrolling and
// forward the ref explicitly.
const DndScrollingDiv = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, forwardedRef) => {
  const innerRef = useRef<HTMLDivElement | null>(null)
  useDndScrolling(innerRef, {
    verticalStrength: vStrength,
    strengthMultiplier: 40,
  })
  return (
    <div
      {...props}
      ref={el => {
        innerRef.current = el
        if (typeof forwardedRef === 'function') {
          forwardedRef(el)
        } else if (forwardedRef) {
          Object.assign(forwardedRef, { current: el })
        }
      }}
      className="scrolling-component"
    />
  )
})

DndScrollingDiv.displayName = 'DndScrollingDiv'

type Props = {
  children: React.ReactNode
  className?: string
  autoHeight?: boolean
  autoHeightMax?: number
  autoHide?: boolean
  vertical?: boolean
  horizontal?: boolean
  color?: 'light' | 'dark'
  onScroll?: (...args: Array<any>) => any
  dndScrolling?: boolean
}
/**
 * A customizable scrollbar component with drag-and-drop support
 * @param children - The content to be scrolled
 * @param className - Optional CSS class name
 * @param autoHeight - Whether to automatically adjust height to content
 * @param autoHeightMax - Maximum height when using auto height
 * @param autoHide - Whether to hide scrollbar when not needed (defaults to true)
 * @param vertical - Whether to show vertical scrollbar
 * @param horizontal - Whether to show horizontal scrollbar
 * @param color - Color theme for the scrollbar ('light' or 'dark', defaults to 'dark')
 * @param onScroll - Callback function for scroll events
 * @param dndScrolling - Whether to enable drag-and-drop scrolling (defaults to false)
 * @returns A custom scrollbar component
 */
export default class CustomScrollbars extends React.Component<Props> {
  _scrollbars: any
  static defaultProps: {
    autoHide: boolean
    color: 'light' | 'dark'
    dndScrolling: boolean
  } = {
    autoHide: true,
    color: 'dark',
    dndScrolling: false,
  }
  scrollbarsStyle: (
    vertical: any,
    horizontal: any,
  ) => {
    height: string
    width: string
  } = memoize((vertical, horizontal) => {
    return Object.assign(
      {},
      vertical
        ? {
            height: '100%',
          }
        : undefined,
      horizontal
        ? {
            width: '100%',
          }
        : undefined,
    )
  })
  thumbStyle: (style: any, color: any) => any = memoize((style, color) => {
    const thumbStyle = {
      backgroundColor:
        color === 'light'
          ? 'rgba(250, 250, 250, 0.5)'
          : 'rgba(50, 50, 50, 0.5)',
      borderRadius: '1rem',
      width: '6px',
      zIndex: 1000,
    }
    return { ...style, ...thumbStyle }
  })
  renderView: (props: React.HTMLAttributes<HTMLDivElement>) => React.ReactNode =
    props => {
      if (this.props.dndScrolling) {
        return <DndScrollingDiv {...props} />
      }

      return <div {...props} className="scrolling-component" />
    }
  renderThumb: (
    arg0: any,
  ) => React.ReactElement<React.ComponentProps<'div'>, 'div'> = ({
    style,
    ...props
  }) => {
    const thumbStyle = this.thumbStyle(style, this.props.color)
    return <div style={thumbStyle} {...props} />
  }

  scrollTop(value: number) {
    if (this._scrollbars) this._scrollbars.scrollTop(value)
  }

  render(): React.ReactNode {
    const style = this.scrollbarsStyle(
      this.props.vertical,
      this.props.horizontal,
    )
    return (
      <Scrollbars
        className={this.props.className}
        thumbSize={100}
        autoHeight={this.props.autoHeight}
        autoHeightMax={this.props.autoHeightMax}
        autoHide={this.props.autoHide}
        style={style}
        renderThumbVertical={this.renderThumb}
        renderThumbHorizontal={this.renderThumb}
        renderView={this.renderView}
        onScroll={this.props.onScroll}
        ref={r => {
          this._scrollbars = r
        }}
      >
        {this.props.children}
      </Scrollbars>
    )
  }
}
/**
 * A native scrollbar component with drag-and-drop support
 * @param children - The content to be scrolled
 * @param className - Optional CSS class name
 * @param autoHeight - Whether to automatically adjust height to content
 * @param autoHeightMax - Maximum height when using auto height
 * @param autoHide - Whether to hide scrollbar when not needed
 * @param vertical - Whether to show vertical scrollbar
 * @param horizontal - Whether to show horizontal scrollbar
 * @param color - Color theme for the scrollbar
 * @param onScroll - Callback function for scroll events
 * @param dndScrolling - Unused in NativeScrollbars (but accepted for compatibility)
 * @returns A native scrollbar component with drag-and-drop functionality
 */
export const NativeScrollbars = (props: Props): React.ReactNode => {
  // dndScrolling is unused in NativeScrollbars - but used in CustomScrollbars
  const { children, dndScrolling, ...rest } = props
  return (
    <ScrollingComponent
      verticalStrength={vStrength}
      strengthMultiplier={40}
      {...rest}
      className="native-scrolling-component"
    >
      {children}
    </ScrollingComponent>
  )
}
